import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Search, X, Loader2 } from 'lucide-react';

/** The TariffCode fields the picker needs (structurally matches the SDK model). */
export interface TariffOption {
  tariffCode: string;
  description?: string;
  headingDescription?: string | null;
  importDutyRate?: number;
  gctRate?: number;
}

export interface TariffCategoryOption {
  name: string;
  icon?: string;
  chapters?: string[];
}

export interface TariffCodePickerLabels {
  label: string;
  placeholder: string;
  hint: string;
  searching: string;
  noResults: string;
  loadError: string;
  change: string;
  duty: string;
  gct: string;
}

const DEFAULT_LABELS: TariffCodePickerLabels = {
  label: 'What are you shipping?',
  placeholder: 'Search an item (e.g. laptop, sneakers) or a tariff code',
  hint: 'Pick the closest match to estimate customs.',
  searching: 'Searching…',
  noResults: 'No match. Try a broader word, or pick a category.',
  loadError: "Couldn't load tariff codes. Please try again.",
  change: 'Change',
  duty: 'duty',
  gct: 'GCT',
};

export interface TariffCodePickerProps<T extends TariffOption = TariffOption> {
  search: (q: string, categoryName?: string) => Promise<T[]>;
  categories: TariffCategoryOption[];
  value: T | null;
  onChange: (code: T | null) => void;
  labels?: Partial<TariffCodePickerLabels>;
  debounceMs?: number;
  className?: string;
}

export function formatTariffCode(code: string): string {
  if (!/^\d{10}$/.test(code || '')) return code;
  return `${code.slice(0, 4)}.${code.slice(4, 6)}.${code.slice(6, 8)}.${code.slice(8, 10)}`;
}

export function formatRatePercent(rate?: number | null): string {
  const pct = Math.round((Number(rate) || 0) * 10000) / 100;
  return `${pct}%`;
}

export function TariffCodePicker<T extends TariffOption = TariffOption>({
  search,
  categories,
  value,
  onChange,
  labels,
  debounceMs = 300,
  className = '',
}: TariffCodePickerProps<T>) {
  const l = { ...DEFAULT_LABELS, ...labels };
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [results, setResults] = useState<T[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [active, setActive] = useState(-1);
  const requestId = useRef(0);

  const trimmed = query.trim();
  const shouldSearch = trimmed.length >= 2 || !!category;

  useEffect(() => {
    if (value || !shouldSearch) {
      requestId.current++;
      setResults([]);
      setStatus('idle');
      return undefined;
    }
    const id = ++requestId.current;
    const timer = setTimeout(() => {
      setStatus('loading');
      search(trimmed.length >= 2 ? trimmed : '', category)
        .then((rows) => {
          if (id !== requestId.current) return;
          setResults(rows || []);
          setActive(-1);
          setStatus('done');
        })
        .catch(() => {
          if (id !== requestId.current) return;
          setResults([]);
          setStatus('error');
        });
    }, debounceMs);
    return () => clearTimeout(timer);
    // `search` is intentionally omitted: callers often pass an inline function.
  }, [trimmed, category, value, debounceMs, shouldSearch]);

  const select = (row: T) => {
    onChange(row);
    setQuery('');
    setResults([]);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // The picker usually sits inside a calculator <form>: Enter picks, never submits.
    if (e.key === 'Enter') e.preventDefault();
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      select(results[active >= 0 ? active : 0]);
    }
  };

  const rateLine = (row: TariffOption) =>
    `${formatRatePercent(row.importDutyRate)} ${l.duty} · ${formatRatePercent(row.gctRate)} ${l.gct}`;

  if (value) {
    return (
      <div className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 ${className}`}>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{l.label}</div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-gray-900 dark:text-white">{value.description}</div>
            {value.headingDescription && (
              <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{value.headingDescription}</div>
            )}
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              <span className="font-mono">{formatTariffCode(value.tariffCode)}</span> · {rateLine(value)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-200 hover:underline"
          >
            <X className="w-4 h-4" aria-hidden="true" />
            {l.change}
          </button>
        </div>
      </div>
    );
  }

  const listId = 'tariff-code-results';

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="tariff-code-search">
        {l.label}
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
        <input
          id="tariff-code-search"
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={l.placeholder}
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
        />
        {status === 'loading' && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" aria-label={l.searching} />
        )}
      </div>

      {categories.length > 0 && (
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
          {categories.map((c) => {
            const pressed = category === c.name;
            return (
              <button
                key={c.name}
                type="button"
                aria-pressed={pressed}
                onClick={() => setCategory(pressed ? undefined : c.name)}
                className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  pressed
                    ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      {status === 'idle' && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{l.hint}</p>}
      {status === 'error' && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{l.loadError}</p>}
      {status === 'done' && results.length === 0 && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{l.noResults}</p>
      )}

      {results.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800"
        >
          {results.map((row, i) => (
            <li
              key={row.tariffCode}
              role="option"
              aria-selected={i === active}
              tabIndex={-1}
              onClick={() => select(row)}
              onMouseEnter={() => setActive(i)}
              className={`cursor-pointer px-3 py-2 ${i === active ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
            >
              <div className="text-sm font-medium text-gray-900 dark:text-white">{row.description}</div>
              {row.headingDescription && (
                <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{row.headingDescription}</div>
              )}
              <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-gray-600 dark:text-gray-300">
                <span className="font-mono">{formatTariffCode(row.tariffCode)}</span>
                <span>{rateLine(row)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default TariffCodePicker;
